import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL = 500;

// Read trigger key from URL: /obs-overlay?key=abc123
const getKeyFromUrl = () => new URLSearchParams(window.location.search).get('key') || '';

const TRANSITION_CSS = `
@keyframes ayo-fade-in { from { opacity: 0 } to { opacity: 1 } }
@keyframes ayo-fade-out { from { opacity: 1 } to { opacity: 0 } }
@keyframes ayo-wipe-in { from { clip-path: inset(0 100% 0 0); opacity: 1 } to { clip-path: inset(0 0 0 0); opacity: 1 } }
@keyframes ayo-wipe-out { from { clip-path: inset(0 0 0 0); opacity: 1 } to { clip-path: inset(0 0 0 100%); opacity: 1 } }
@keyframes ayo-glitch-in {
  0% { opacity: 0; transform: translate3d(-18px,0,0) scaleY(1.06); filter: hue-rotate(100deg) saturate(4) }
  15% { opacity: 1; transform: translate3d(14px,0,0) scaleY(0.96) }
  30% { transform: translate3d(-8px,2px,0); filter: hue-rotate(-40deg) saturate(2) }
  45% { transform: translate3d(6px,-2px,0); filter: none }
  60% { transform: translate3d(-3px,0,0) }
  100% { opacity: 1; transform: none; filter: none }
}
@keyframes ayo-glitch-out {
  0% { opacity: 1; transform: none }
  25% { transform: translate3d(10px,-2px,0) scaleY(1.04); filter: hue-rotate(80deg) saturate(3) }
  55% { transform: translate3d(-14px,2px,0) }
  100% { opacity: 0; transform: translate3d(24px,0,0) scaleY(0.9) }
}
@keyframes ayo-lt-in { from { opacity: 0; transform: translateY(120%) } to { opacity: 1; transform: translateY(0) } }
@keyframes ayo-lt-out { from { opacity: 1; transform: translateY(0) } to { opacity: 0; transform: translateY(120%) } }
@keyframes ayo-dot { 0%,100% { opacity: 1 } 50% { opacity: 0.25 } }
@keyframes ayo-qr-in { from { opacity: 0; transform: translateY(18px) scale(0.94) } to { opacity: 1; transform: none } }
`;

const animFor = (style, dir) => {
  const t = style === 'wipe' ? 'wipe' : style === 'glitch' ? 'glitch' : 'fade';
  return `ayo-${t}-${dir} ${dir === 'in' ? '520ms' : '440ms'} ${dir === 'in' ? 'ease-out' : 'ease-in'} both`;
};

const fmtCountdown = (s) => `${Math.floor(Math.max(s, 0) / 60)}:${String(Math.max(s, 0) % 60).padStart(2, '0')}`;

export default function ObsOverlay() {
  const [state, setState] = useState('idle');

  // Hide any platform badges (e.g. "Made with Emergent") on the overlay page
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = '[data-emergent-badge], [class*="emergent"], a[href*="emergent"] { display: none !important; } html, body, #root { background: transparent !important; }' + TRANSITION_CSS;
    document.head.appendChild(style);
    // Also try to remove directly
    const interval = setInterval(() => {
      document.querySelectorAll('a[href*="emergent"], [data-emergent-badge]').forEach(el => el.remove());
    }, 500);
    return () => { clearInterval(interval); style.remove(); };
  }, []);
  const [currentAd, setCurrentAd] = useState(null);
  const [holdingImageUrl, setHoldingImageUrl] = useState(null);
  const [holdingType, setHoldingType] = useState('image');  // image or html
  const [visible, setVisible] = useState(false);
  const [breakId, setBreakId] = useState(null);
  const [overlayAd, setOverlayAd] = useState(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayPosition, setOverlayPosition] = useState('bottom-right');
  const [testPattern, setTestPattern] = useState(false);
  const [transition, setTransition] = useState('fade');
  const [holdingCountdown, setHoldingCountdown] = useState(0);
  const [holdingQr, setHoldingQr] = useState(null);
  const [streamerId, setStreamerId] = useState(null);
  const [adPlaying, setAdPlaying] = useState(false);
  const [prewarmUrls, setPrewarmUrls] = useState([]);
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const processedBreaks = useRef(new Set());
  const processedOverlays = useRef(new Set());
  // Use refs for values that need to be current in callbacks
  const stateRef = useRef('idle');
  const breakIdRef = useRef(null);
  const endAfterCurrentRef = useRef(false);
  const holdingShownCountdown = useRef(false);

  const getFullUrl = (url) => url?.startsWith('http') ? url : `${BACKEND_URL}${url}`;

  // Keep refs in sync
  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { breakIdRef.current = breakId; }, [breakId]);

  const endBreak = useCallback(() => {
    setVisible(false);
    endAfterCurrentRef.current = false;
    const bid = breakIdRef.current;
    setTimeout(() => {
      setState('idle');
      stateRef.current = 'idle';
      setCurrentAd(null);
      setHoldingImageUrl(null);
      setHoldingQr(null);
      setHoldingCountdown(0);
      holdingShownCountdown.current = false;
      if (bid) {
        axios.post(`${API}/ads/break/${bid}/ended`).catch(() => {});
      }
      setBreakId(null);
      breakIdRef.current = null;
    }, 500);
  }, []);

  const onAdFinished = useCallback(async () => {
    const bid = breakIdRef.current;
    if (!bid) return;

    // If "back to stream" was requested, end the break now
    if (endAfterCurrentRef.current) {
      endBreak();
      return;
    }

    try {
      const res = await axios.post(`${API}/ads/break/${bid}/ad-complete`);
      if (res.data.status === 'next') {
        setCurrentAd(res.data.ad);
      } else if (res.data.status === 'holding') {
        setState('holding');
        stateRef.current = 'holding';
        setCurrentAd(null);
        setHoldingImageUrl(res.data.holdingImageUrl);
        setHoldingQr(res.data.holdingQr || null);
        setHoldingCountdown(res.data.holdingCountdown || 0);
        holdingShownCountdown.current = !!res.data.holdingCountdown;
      } else if (res.data.status === 'ended') {
        endBreak();
      }
    } catch (err) {
      console.error('Failed to advance break:', err);
      endBreak();
    }
  }, [endBreak]);

  const startBreak = useCallback((data) => {
    if (processedBreaks.current.has(data.breakId)) return;
    processedBreaks.current.add(data.breakId);
    setBreakId(data.breakId);
    breakIdRef.current = data.breakId;
    setHoldingImageUrl(data.holdingImageUrl);
    endAfterCurrentRef.current = false;
    if (data.ads && data.ads.length > 0) {
      setCurrentAd(data.ads[0]);
      setState('playing');
      stateRef.current = 'playing';
      setTimeout(() => setVisible(true), 50);
    }
  }, []);

  const showOverlay = useCallback((data) => {
    if (processedOverlays.current.has(data.triggerId)) return;
    processedOverlays.current.add(data.triggerId);
    setOverlayAd(data.ad);
    if (data.streamerId) setStreamerId(data.streamerId);
    setOverlayPosition(data.position || 'bottom-right');
    setTimeout(() => setOverlayVisible(true), 50);
    const dur = (data.displayDuration || 10) * 1000;
    setTimeout(() => {
      setOverlayVisible(false);
      setTimeout(() => {
        setOverlayAd(null);
        if (data.triggerId) axios.post(`${API}/overlay/${data.triggerId}/complete`).catch(() => {});
      }, 500);
    }, dur);
  }, []);

  // WebSocket — use refs to avoid stale closures
  useEffect(() => {
    const wsUrl = BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    if (!wsUrl) return;
    const key = getKeyFromUrl();
    const connect = () => {
      try {
        const wsEndpoint = key ? `${wsUrl}/api/ws/triggers?key=${key}` : `${wsUrl}/api/ws/triggers`;
        const ws = new WebSocket(wsEndpoint);
        ws.onopen = () => console.log('OBS WS connected');
        ws.onmessage = (event) => {
          try {
            const d = JSON.parse(event.data);
            if (d.type === 'prewarm' && Array.isArray(d.urls)) {
              setPrewarmUrls(prev => {
                const merged = [...new Set([...d.urls, ...prev])].slice(0, 6);
                return JSON.stringify(merged) === JSON.stringify(prev) ? prev : merged;
              });
              if (d.streamerId) setStreamerId(sid => sid || d.streamerId);
            }
            if (d.type === 'ad_break_start') {
              setHoldingType(d.holdingType || 'image');
              if (d.streamerId) setStreamerId(d.streamerId);
              setHoldingQr(d.holdingQr || null);
              if (d.transition) setTransition(d.transition);
              setHoldingCountdown(d.holdingCountdown || 0);
              holdingShownCountdown.current = !!d.holdingCountdown;
              if (d.skipToHolding) {
                // No ads — go straight to holding screen
                processedBreaks.current.add(d.breakId);
                setBreakId(d.breakId);
                breakIdRef.current = d.breakId;
                setHoldingImageUrl(d.holdingImageUrl);
                setState('holding');
                stateRef.current = 'holding';
              } else {
                startBreak(d);
              }
            }
            if (d.type === 'ad_break_holding') {
              setState('holding');
              stateRef.current = 'holding';
              setCurrentAd(null);
              setHoldingImageUrl(d.holdingImageUrl);
              if (d.streamerId) setStreamerId(d.streamerId);
              setHoldingQr(d.holdingQr || null);
              if (d.holdingCountdown) { setHoldingCountdown(d.holdingCountdown); holdingShownCountdown.current = true; }
            }
            if (d.type === 'back_to_stream') {
              const curState = stateRef.current;
              if (d.finishCurrent && curState === 'playing') {
                // Flag: end break after current ad finishes
                endAfterCurrentRef.current = true;
              } else {
                // Holding or no current ad — end immediately
                endBreak();
              }
            }
            if (d.type === 'break_ended') endBreak();
            if (d.type === 'ad_trigger' && stateRef.current === 'idle') {
              setCurrentAd(d.ad);
              setState('playing');
              stateRef.current = 'playing';
              setBreakId(d.triggerId);
              breakIdRef.current = d.triggerId;
              setTimeout(() => setVisible(true), 50);
            }
            if (d.type === 'overlay_trigger') showOverlay(d);
            if (d.type === 'test_pattern') {
              setTestPattern(true);
              setTimeout(() => setTestPattern(false), (d.duration || 3) * 1000);
            }
          } catch (err) { console.error('WS parse:', err); }
        };
        ws.onclose = () => setTimeout(connect, 3000);
        wsRef.current = ws;
      } catch { setTimeout(connect, 3000); }
    };
    connect();
    return () => wsRef.current?.close();
  }, [startBreak, endBreak, showOverlay]);

  // Polling fallback
  useEffect(() => {
    const key = getKeyFromUrl();
    const poll = async () => {
      if (stateRef.current !== 'idle') return;
      try {
        const res = await axios.get(`${API}/ads/current-break${key ? `?key=${key}` : ''}`);
        if (res.data && !processedBreaks.current.has(res.data.id)) {
          setStreamerId(res.data.streamerId || null);
          setHoldingQr(res.data.holdingQr || null);
          if (res.data.transition) setTransition(res.data.transition);
          startBreak({ breakId: res.data.id, ads: res.data.ads, holdingImageUrl: res.data.holdingImageUrl });
        }
      } catch {}
    };
    const interval = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [startBreak]);

  // Polling fallback for back-to-stream (in case WS missed it)
  useEffect(() => {
    const key = getKeyFromUrl();
    const poll = async () => {
      if (stateRef.current === 'idle') return;
      const bid = breakIdRef.current;
      if (!bid) return;
      try {
        const res = await axios.get(`${API}/ads/current-break${key ? `?key=${key}` : ''}`);
        if (!res.data || res.data.status === 'ended') {
          endBreak();
        } else if (res.data.status === 'ending') {
          endAfterCurrentRef.current = true;
        }
      } catch {}
    };
    const interval = setInterval(poll, 1000);
    return () => clearInterval(interval);
  }, [endBreak]);

  const failedAdRef = useRef(null);

  const onAdFailed = useCallback(async () => {
    const bid = breakIdRef.current;
    if (!bid) return;
    try {
      const res = await axios.post(`${API}/ads/break/${bid}/ad-failed`);
      if (res.data.status === 'next') {
        setCurrentAd(res.data.ad);
      } else if (res.data.status === 'holding') {
        setState('holding');
        stateRef.current = 'holding';
        setCurrentAd(null);
        setHoldingImageUrl(res.data.holdingImageUrl);
        setHoldingQr(res.data.holdingQr || null);
        setHoldingCountdown(res.data.holdingCountdown || 0);
        holdingShownCountdown.current = !!res.data.holdingCountdown;
      } else if (res.data.status === 'ended') {
        endBreak();
      }
    } catch {
      endBreak();
    }
  }, [API, endBreak]);

  const handleVideoEnd = () => onAdFinished();
  const handleVideoError = () => {
    // a <video> can fire error more than once while its src is swapped out
    const id = currentAd?.adId;
    if (failedAdRef.current === id) return;
    failedAdRef.current = id;
    onAdFailed();
  };

  // The QR only belongs on screen while the ad is actually playing, never on its own
  useEffect(() => { setAdPlaying(false); }, [currentAd?.adId]);

  useEffect(() => {
    if (state !== 'playing' || !currentAd?.mimeType?.startsWith('video/') || adPlaying) return;
    // never sit on a black frame: skip the creative without billing the brand
    const t = setTimeout(() => handleVideoError(), 9000);
    return () => clearTimeout(t);
  }, [currentAd, state, adPlaying, onAdFailed]);

  // Autoplay policy fallback: retry muted rather than dropping the spot entirely
  const startPlayback = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {
      v.muted = true;
      v.play().catch(err => console.warn('[AYO overlay] playback blocked:', err?.name || err));
    });
  }, []);

  // Holding screen "back in" countdown
  useEffect(() => {
    if (state !== 'holding' || holdingCountdown <= 0) return;
    const t = setInterval(() => setHoldingCountdown(v => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [state, holdingCountdown]);

  // Image ad timer
  useEffect(() => {
    if (state === 'playing' && currentAd && !currentAd.mimeType?.startsWith('video/') && visible) {
      setAdPlaying(true);
      const dur = (currentAd.slotDuration || 5) * 1000;
      const timer = setTimeout(() => onAdFinished(), dur);
      return () => clearTimeout(timer);
    }
  }, [currentAd, state, visible, onAdFinished]);

  const qrLink = (code, surface) => {
    const p = new URLSearchParams();
    if (streamerId) p.set('c', streamerId);
    if (breakIdRef.current) p.set('b', breakIdRef.current);
    p.set('s', surface);
    return `${BACKEND_URL}/api/r/${code}?${p.toString()}`;
  };

  const QrCard = ({ code, caption, surface, size = 168 }) => {
    if (!code) return null;
    return (
      <div style={{ position: 'fixed', bottom: '48px', right: '48px', zIndex: 100002, animation: 'ayo-qr-in 620ms cubic-bezier(0.16,1,0.3,1) both' }}>
        <div style={{ padding: '16px', borderRadius: '16px', background: 'rgba(8,8,8,0.94)', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', boxShadow: '0 26px 70px rgba(0,0,0,0.8), inset 0 0 0 1px rgba(255,255,255,0.09)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <div style={{ background: '#fff', padding: '10px', borderRadius: '10px', lineHeight: 0 }}>
            <QRCodeSVG value={qrLink(code, surface)} size={size} level="M" />
          </div>
          {caption ? (
            <div style={{ padding: '7px 16px', borderRadius: '999px', background: '#FF3B30', color: '#fff', fontSize: '13px', fontFamily: 'monospace', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', maxWidth: `${size + 20}px`, textAlign: 'center' }}>{caption}</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#FF3B30', animation: 'ayo-dot 1.4s ease-in-out infinite' }} />
              <span style={{ fontSize: '11px', fontFamily: 'monospace', color: '#8a8a8a', textTransform: 'uppercase', letterSpacing: '0.22em' }}>Scan me</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const OverlayBadge = () => {
    if (!overlayAd) return null;
    const ltQr = overlayAd.qrEnabled && overlayAd.qrShortCode && (overlayAd.qrOverride || !['flagged', 'unreachable'].includes(overlayAd.qrStatus)) && (overlayAd.qrPlacements || []).includes('lower-third') ? overlayAd : null;
    if (overlayPosition === 'lower-third') {
      return (
        <div style={{ position: 'fixed', bottom: '70px', left: '5%', width: '90%', zIndex: 100000, animation: overlayVisible ? 'ayo-lt-in 600ms cubic-bezier(0.16,1,0.3,1) both' : 'ayo-lt-out 400ms ease-in both' }}>
          <div style={{ display: 'flex', alignItems: 'stretch', height: '116px', borderRadius: '10px', overflow: 'hidden', background: 'rgba(8,8,8,0.95)', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', boxShadow: '0 24px 70px rgba(0,0,0,0.75), inset 0 0 0 1px rgba(255,255,255,0.08)' }}>
            <div style={{ width: '6px', background: '#FF3B30' }} />
            <div style={{ height: '100%', aspectRatio: '16 / 9', background: '#000', flexShrink: 0 }}>
              <img src={getFullUrl(overlayAd.url)} alt="Sponsored" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </div>
            <div style={{ flex: 1, padding: '16px 22px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '6px', minWidth: 0 }}>
              <span style={{ fontSize: '11px', fontFamily: 'monospace', color: '#FF3B30', textTransform: 'uppercase', letterSpacing: '0.22em', fontWeight: 700 }}>Sponsored By</span>
              <span style={{ fontSize: '26px', fontWeight: 900, color: '#fff', letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {(overlayAd.brandName || overlayAd.creatorName || overlayAd.displayName || overlayAd.filename || '').replace(/\.[a-z0-9]+$/i, '')}
              </span>
            </div>
            {ltQr && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderLeft: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ background: '#fff', padding: '6px', borderRadius: '8px', lineHeight: 0 }}>
                  <QRCodeSVG value={qrLink(ltQr.qrShortCode, 'lower-third')} size={84} level="M" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxWidth: '190px' }}>
                  <span style={{ fontSize: '10px', fontFamily: 'monospace', color: '#8a8a8a', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Scan</span>
                  <span style={{ fontSize: '14px', fontWeight: 800, color: '#fff', lineHeight: 1.2, textTransform: 'uppercase', letterSpacing: '0.02em' }}>{ltQr.qrCaption || 'Scan for the offer'}</span>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', padding: '0 20px', gap: '8px' }}>
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#FF3B30', animation: 'ayo-dot 1.4s ease-in-out infinite' }} />
              <span style={{ fontSize: '10px', fontFamily: 'monospace', color: '#777', textTransform: 'uppercase', letterSpacing: '0.18em' }}>AYO Source</span>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div style={{ position: 'fixed', bottom: '40px', right: '40px', zIndex: 100000, opacity: overlayVisible ? 1 : 0, transition: 'opacity 500ms ease-in-out, transform 500ms ease-in-out', transform: overlayVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)' }}>
        <div style={{ width: '280px', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.1)', background: '#121212' }}>
          <img src={getFullUrl(overlayAd.url)} alt="Sponsored" style={{ width: '100%', height: 'auto', display: 'block' }} />
          <div style={{ padding: '6px 10px', background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '9px', fontFamily: 'monospace', color: '#666', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Sponsored</span>
            <span style={{ fontSize: '9px', fontFamily: 'monospace', color: '#444' }}>AD</span>
          </div>
        </div>
      </div>
    );
  };

  // TEST PATTERN
  if (testPattern) {
    return (
      <div style={{ width: '100vw', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 99999, background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ display: 'flex', gap: '4px', marginBottom: '30px' }}>
          {['#FF3B30', '#FF9500', '#FFCC00', '#34C759', '#007AFF', '#5856D6', '#AF52DE'].map((c, i) => (
            <div key={i} style={{ width: '80px', height: '200px', backgroundColor: c }} />
          ))}
        </div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: 'monospace', fontSize: '48px', fontWeight: 900, color: '#fff', letterSpacing: '0.1em' }}>AYO SOURCE</p>
          <p style={{ fontFamily: 'monospace', fontSize: '16px', color: '#FF3B30', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Test Pattern — Connection Verified</p>
          <p style={{ fontFamily: 'monospace', fontSize: '12px', color: '#666', marginTop: '10px' }}>This will disappear in 3 seconds</p>
        </div>
      </div>
    );
  }

  if (state === 'idle') {
    return <div style={{ background: 'transparent', width: '100vw', height: '100vh' }}><OverlayBadge /></div>;
  }

  if (state === 'holding') {
    return (
      <div style={{ width: '100vw', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 99999, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: animFor(transition, 'in') }}>
        {holdingImageUrl ? (
          holdingType === 'html' ? (
            <iframe src={getFullUrl(holdingImageUrl)} title="Holding Screen" style={{ width: '100%', height: '100%', border: 'none' }} sandbox="allow-scripts allow-same-origin" />
          ) : (
            <img src={getFullUrl(holdingImageUrl)} alt="Holding" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          )
        ) : (
          <div style={{ color: '#333', fontFamily: 'monospace', fontSize: '14px' }}>HOLDING</div>
        )}
        {holdingCountdown > 0 || holdingShownCountdown.current ? (
          <div style={{ position: 'fixed', top: '48px', right: '48px', zIndex: 100001, display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 24px', borderRadius: '999px', background: 'rgba(8,8,8,0.82)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', boxShadow: '0 18px 50px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255,255,255,0.09)' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#FF3B30', animation: 'ayo-dot 1.4s ease-in-out infinite' }} />
            <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#8a8a8a', textTransform: 'uppercase', letterSpacing: '0.24em' }}>
              {holdingCountdown > 0 ? 'Back in' : 'Back'}
            </span>
            <span style={{ fontSize: '30px', fontWeight: 900, color: '#fff', fontFamily: 'monospace', lineHeight: 1, letterSpacing: '-0.02em' }}>
              {holdingCountdown > 0 ? fmtCountdown(holdingCountdown) : 'SHORTLY'}
            </span>
          </div>
        ) : null}
        {holdingQr ? <QrCard code={holdingQr.code} caption={holdingQr.caption} surface="holding" size={190} /> : null}
        <OverlayBadge />
      </div>
    );
  }

  const isVideo = currentAd?.mimeType?.startsWith('video/');
  return (
    <div style={{ width: '100vw', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 99999, overflow: 'hidden', background: 'transparent' }}>
      <div style={{ width: '100%', height: '100%', animation: animFor(transition, visible ? 'in' : 'out'), background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isVideo ? (
          <video ref={videoRef} src={getFullUrl(currentAd.url)} autoPlay playsInline preload="auto" onLoadedMetadata={startPlayback} onPlaying={() => setAdPlaying(true)} onEnded={handleVideoEnd} onError={handleVideoError} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : (
          <img src={getFullUrl(currentAd?.url)} alt="Ad" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={handleVideoError} />
        )}
      </div>
      {visible && adPlaying && currentAd?.qrEnabled && currentAd?.qrShortCode && (currentAd.qrPlacements || []).includes('fullscreen')
        ? <QrCard code={currentAd.qrShortCode} caption={currentAd.qrCaption} surface="fullscreen" size={150} />
        : null}
      <OverlayBadge />
      {/* Hidden pre-buffered copies of upcoming video ads — keeps breaks from opening on a blank screen */}
      {prewarmUrls.map(u => (
        <video key={u} src={getFullUrl(u)} preload="auto" muted playsInline
          style={{ position: 'fixed', width: 1, height: 1, opacity: 0, pointerEvents: 'none', left: -9999, top: -9999 }} />
      ))}
    </div>
  );
}
